import { productosService } from './productosService';
import { clientesService } from './clientesService';
import { ventasService } from './ventasService';
import { cotizacionesService } from './cotizacionesService';
import { facturasService } from './facturasService';
import { categoriasService } from './categoriasService';
import { sucursalesService } from './sucursalesService';
import { empresasService } from './empresasService';
import { inventarioService } from './inventarioService';
import { empleadoService } from './empleadoService';

/**
 * Servicio de búsqueda global
 */
export const searchService = {
  /**
   * Buscar en todo el sistema
   * @param {string} query - Término de búsqueda
   * @param {number} empresaId - ID de la empresa (opcional)
   * @param {number} sucursalId - ID de la sucursal (opcional)
   * @param {function} canView - Función para verificar permisos (opcional)
   */
  search: async (query, empresaId = null, sucursalId = null, canView = null) => {
    if (!query || query.trim().length < 2) {
      return {
        success: true,
        data: {
          modulos: [],
          empleados: [],
          productos: [],
          clientes: [],
          cotizaciones: [],
          ventas: [],
          facturas: [],
          categorias: [],
          sucursales: [],
        },
      };
    }

    const searchTerm = query.toLowerCase().trim();
    const results = {
      modulos: [],
      empleados: [],
      productos: [],
      clientes: [],
      cotizaciones: [],
      ventas: [],
      facturas: [],
      categorias: [],
      sucursales: [],
    };

    try {
      // Buscar módulos del sistema
      const modulos = [
        { name: 'Dashboard', path: '/admin/dashboard', icon: 'tim-icons icon-chart-pie-36', category: 'Módulos', modulo: 'Dashboard' },
        { name: 'Empresas', path: '/admin/empresas', icon: 'tim-icons icon-bank', category: 'Configuración', modulo: 'Empresas' },
        { name: 'Sucursales', path: '/admin/sucursales', icon: 'tim-icons icon-shop', category: 'Configuración', modulo: 'Sucursales' },
        { name: 'Empleados', path: '/admin/empleados', icon: 'tim-icons icon-badge', category: 'Configuración', modulo: 'Empleados' },
        { name: 'Productos', path: '/admin/productos', icon: 'tim-icons icon-box-2', category: 'Productos', modulo: 'Productos' },
        { name: 'Categorías', path: '/admin/categorias', icon: 'tim-icons icon-tag', category: 'Productos', modulo: 'Categorias' },
        { name: 'Inventario', path: '/admin/inventario', icon: 'tim-icons icon-archive-2', category: 'Módulos', modulo: 'Inventario' },
        { name: 'Clientes', path: '/admin/clientes', icon: 'tim-icons icon-single-02', category: 'Módulos', modulo: 'Clientes' },
        { name: 'Cotizaciones', path: '/admin/cotizaciones', icon: 'tim-icons icon-notes', category: 'Módulos', modulo: 'Cotizaciones' },
        { name: 'Ventas', path: '/admin/ventas', icon: 'tim-icons icon-cart', category: 'Módulos', modulo: 'Ventas' },
        { name: 'Facturación', path: '/admin/facturas', icon: 'tim-icons icon-paper', category: 'Módulos', modulo: 'Facturacion' },
        { name: 'Usuarios', path: '/admin/usuarios', icon: 'tim-icons icon-single-02', category: 'Administración', modulo: 'Usuarios' },
        { name: 'Roles', path: '/admin/roles', icon: 'tim-icons icon-badge', category: 'Administración', modulo: 'Roles' },
        { name: 'Permisos', path: '/admin/permisos', icon: 'tim-icons icon-key-25', category: 'Administración', modulo: 'Permisos' },
        { name: 'Reportes', path: '/admin/reportes', icon: 'tim-icons icon-chart-bar-32', category: 'Módulos', modulo: 'Reportes' },
        { name: 'Auditoría', path: '/admin/auditoria', icon: 'tim-icons icon-notes', category: 'Módulos', modulo: 'Auditoria' },
      ];

      // Filtrar módulos por búsqueda y permisos
      let modulosFiltrados = modulos.filter(modulo => 
        modulo.name.toLowerCase().includes(searchTerm) ||
        modulo.category.toLowerCase().includes(searchTerm)
      );

      // Si hay función canView, filtrar por permisos
      if (canView && typeof canView === 'function') {
        modulosFiltrados = modulosFiltrados.filter(modulo => {
          // Dashboard siempre está disponible
          if (modulo.modulo === 'Dashboard') return true;
          // Verificar permiso para el módulo
          return canView(modulo.modulo);
        });
      }

      results.modulos = modulosFiltrados;

      // Buscar productos (solo si tiene permiso)
      if (!canView || canView('Productos')) {
        try {
          const productosResponse = await productosService.getAll(empresaId, sucursalId);
          if (productosResponse.success && productosResponse.data) {
            const productos = Array.isArray(productosResponse.data) ? productosResponse.data : [];
            const productosFiltrados = productos
              .filter(p => {
                const nombre = (p.Nombre || p.nombre || '').toLowerCase();
                const sku = (p.SKU || p.sku || '').toLowerCase();
                const codigoBarras = (p.CodigoBarras || p.codigoBarras || '').toLowerCase();
                return nombre.includes(searchTerm) || sku.includes(searchTerm) || codigoBarras.includes(searchTerm);
              })
              .slice(0, 5); // Limitar a 5 resultados

            // Obtener stock para cada producto si hay sucursal seleccionada
            const productosConStock = await Promise.all(
              productosFiltrados.map(async (p) => {
                const productoBase = {
                  id: p.ProductoID || p.productoID,
                  name: p.Nombre || p.nombre,
                  sku: p.SKU || p.sku,
                  path: `/admin/productos`,
                  type: 'producto',
                  stockActual: null,
                };

                // Si hay sucursal, obtener el stock
                if (sucursalId) {
                  try {
                    const stockResponse = await inventarioService.getStock(
                      productoBase.id,
                      sucursalId
                    );
                    if (stockResponse.success && stockResponse.data) {
                      const stockData = stockResponse.data;
                      productoBase.stockActual = stockData.StockActual !== undefined
                        ? stockData.StockActual
                        : (stockData.stockActual !== undefined ? stockData.stockActual : 0);
                    }
                  } catch (err) {
                  }
                }

                return productoBase;
              })
            );

            results.productos = productosConStock;
          }
        } catch (error) {
        }
      }

      // Buscar clientes (solo si tiene permiso)
      if (!canView || canView('Clientes')) {
        try {
          const clientesResponse = await clientesService.getAll(empresaId, sucursalId);
          if (clientesResponse.success && clientesResponse.data) {
            const clientes = Array.isArray(clientesResponse.data) ? clientesResponse.data : [];
            results.clientes = clientes
              .filter(c => {
                const nombre = (c.NombreRazonSocial || c.nombreRazonSocial || '').toLowerCase();
                const rfc = (c.RFC || c.rfc || '').toLowerCase();
                const correo = (c.CorreoElectronico || c.correoElectronico || '').toLowerCase();
                return nombre.includes(searchTerm) || rfc.includes(searchTerm) || correo.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(c => ({
                id: c.ClienteID || c.clienteID,
                name: c.NombreRazonSocial || c.nombreRazonSocial,
                rfc: c.RFC || c.rfc,
                path: `/admin/clientes`,
                type: 'cliente',
              }));
          }
        } catch (error) {
        }
      }

      // Buscar empleados (solo si tiene permiso)
      if (!canView || canView('Empleados')) {
        try {
          // Asumiendo que el getAll de empleadoService permite parámetros o al menos trae lista
          const empleadosResponse = await empleadoService.getAll(empresaId);
          if (empleadosResponse.success && empleadosResponse.data) {
            const empleados = Array.isArray(empleadosResponse.data) ? empleadosResponse.data : [];
            results.empleados = empleados
              .filter(emp => {
                const nombre = (emp.Nombre || emp.nombre || '').toLowerCase();
                const curp = (emp.CURP || emp.curp || '').toLowerCase();
                const numero = (emp.NumeroEmpleado || emp.numeroEmpleado || '').toLowerCase();
                return nombre.includes(searchTerm) || curp.includes(searchTerm) || numero.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(emp => ({
                id: emp.EmpleadoID || emp.empleadoID,
                name: emp.Nombre || emp.nombre,
                curp: emp.CURP || emp.curp,
                numero: emp.NumeroEmpleado || emp.numeroEmpleado,
                path: `/admin/empleados`,
                type: 'empleado',
              }));
          }
        } catch (error) {
        }
      }

      // Buscar cotizaciones (solo si tiene permiso)
      if (!canView || canView('Cotizaciones')) {
        try {
          const cotizacionesResponse = await cotizacionesService.getAll(sucursalId);
          if (cotizacionesResponse.success && cotizacionesResponse.data) {
            const cotizaciones = Array.isArray(cotizacionesResponse.data) ? cotizacionesResponse.data : [];
            results.cotizaciones = cotizaciones
              .filter(c => {
                const folio = (c.Folio || c.folio || '').toLowerCase();
                const clienteNombre = (c.ClienteNombre || c.clienteNombre || '').toLowerCase();
                return folio.includes(searchTerm) || clienteNombre.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(c => ({
                id: c.CotizacionID || c.cotizacionID,
                name: `Cotización ${c.Folio || c.folio || c.CotizacionID || c.cotizacionID}`,
                cliente: c.ClienteNombre || c.clienteNombre,
                total: c.Total || c.total,
                path: `/admin/cotizaciones`,
                type: 'cotizacion',
              }));
          }
        } catch (error) {
        }
      }

      // Buscar ventas (solo si tiene permiso)
      if (!canView || canView('Ventas')) {
        try {
          const ventasResponse = await ventasService.getAll(sucursalId);
          if (ventasResponse.success && ventasResponse.data) {
            const ventas = Array.isArray(ventasResponse.data) ? ventasResponse.data : [];
            results.ventas = ventas
              .filter(v => {
                const folio = (v.Folio || v.folio || '').toLowerCase();
                const clienteNombre = (v.ClienteNombre || v.clienteNombre || '').toLowerCase();
                return folio.includes(searchTerm) || clienteNombre.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(v => ({
                id: v.VentaID || v.ventaID,
                name: `Venta ${v.Folio || v.folio || v.VentaID || v.ventaID}`,
                cliente: v.ClienteNombre || v.clienteNombre,
                total: v.Total || v.total,
                path: `/admin/ventas`,
                type: 'venta',
              }));
          }
        } catch (error) {
        }
      }

      // Buscar facturas (solo si tiene permiso)
      // Las facturas se obtienen a través de las ventas que tienen factura asociada
      if (!canView || canView('Facturacion')) {
        try {
          const ventasResponse = await ventasService.getAll(sucursalId);
          if (ventasResponse.success && ventasResponse.data) {
            const ventas = Array.isArray(ventasResponse.data) ? ventasResponse.data : [];
            
            // Obtener facturas para las ventas que coincidan con la búsqueda
            const ventasFiltradas = ventas.filter(v => {
              const folio = (v.Folio || v.folio || '').toLowerCase();
              const clienteNombre = (v.ClienteNombre || v.clienteNombre || '').toLowerCase();
              return folio.includes(searchTerm) || clienteNombre.includes(searchTerm);
            }).slice(0, 10); // Obtener más ventas para tener más facturas disponibles

            // Obtener facturas para estas ventas
            const facturasPromises = ventasFiltradas.map(async (venta) => {
              try {
                const facturaResponse = await facturasService.getByVenta(venta.VentaID || venta.ventaID);
                if (facturaResponse.success && facturaResponse.data) {
                  const factura = facturaResponse.data;
                  const uuid = (factura.UUID || factura.uuid || '').toLowerCase();
                  const folioFiscal = (factura.FolioFiscal || factura.folioFiscal || '').toLowerCase();
                  const ventaFolio = (venta.Folio || venta.folio || '').toLowerCase();
                  const clienteNombre = (venta.ClienteNombre || venta.clienteNombre || '').toLowerCase();
                  
                  // Filtrar facturas que coincidan con el término de búsqueda
                  if (uuid.includes(searchTerm) || 
                      folioFiscal.includes(searchTerm) || 
                      ventaFolio.includes(searchTerm) || 
                      clienteNombre.includes(searchTerm)) {
                    return {
                      id: factura.FacturaID || factura.facturaID,
                      name: `Factura ${factura.FolioFiscal || factura.folioFiscal || factura.UUID || factura.uuid || factura.FacturaID || factura.facturaID}`,
                      cliente: venta.ClienteNombre || venta.clienteNombre,
                      total: factura.Total || factura.total || venta.Total || venta.total,
                      uuid: factura.UUID || factura.uuid,
                      estatus: factura.Estatus || factura.estatus,
                      path: `/admin/facturas`,
                      type: 'factura',
                    };
                  }
                }
                return null;
              } catch {
                return null;
              }
            });

            const facturasResultados = await Promise.all(facturasPromises);
            results.facturas = facturasResultados
              .filter(f => f !== null)
              .slice(0, 5); // Limitar a 5 resultados
          }
        } catch (error) {
        }
      }

      // Buscar categorías (solo si tiene permiso)
      if (!canView || canView('Categorias')) {
        try {
          const categoriasResponse = await categoriasService.getAll(empresaId);
          if (categoriasResponse.success && categoriasResponse.data) {
            const categorias = Array.isArray(categoriasResponse.data) ? categoriasResponse.data : [];
            results.categorias = categorias
              .filter(cat => {
                const nombre = (cat.Nombre || cat.nombre || '').toLowerCase();
                return nombre.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(cat => ({
                id: cat.CategoriaID || cat.categoriaID,
                name: cat.Nombre || cat.nombre,
                path: `/admin/categorias`,
                type: 'categoria',
              }));
          }
        } catch (error) {
        }
      }

      // Buscar sucursales (solo si tiene permiso)
      if (!canView || canView('Sucursales')) {
        try {
          const sucursalesResponse = await sucursalesService.getAll(empresaId);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursales = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            results.sucursales = sucursales
              .filter(s => {
                const nombre = (s.Nombre || s.nombre || '').toLowerCase();
                return nombre.includes(searchTerm);
              })
              .slice(0, 5) // Limitar a 5 resultados
              .map(s => ({
                id: s.SucursalID || s.sucursalID,
                name: s.Nombre || s.nombre,
                path: `/admin/sucursales`,
                type: 'sucursal',
              }));
          }
        } catch (error) {
        }
      }

      return {
        success: true,
        data: results,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al realizar la búsqueda',
        data: results,
      };
    }
  },
};

